/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { Handler, makeHandlerChainAwaitable, replaceInChain } from "@insite/client-framework/HandlerCreator";
import { Cart, updateCart as updateCartApi } from "@insite/client-framework/Services/CartService";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import {
    chain,
    ValidateShippingAddress,
} from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/SetUseBillingAddress";
import validateShippingAddressForm from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/ValidateShippingAddressForm";
import { ShipToModel } from "@insite/client-framework/Types/ApiModels";

type HandlerType = Handler<
    {
        useBillingAddress: boolean;
    },
    {
        cart: Cart;
        shipTosForBillTo: ShipToModel[];
        shipToForEditing: ShipToModel;
    }
>;

export const xValidateShippingAddress: HandlerType = async props => {
    const state = props.getState();
    const {
        context: { session, settings },
    } = state;

    const addressFieldsDataView = getAddressFieldsDataView(state);
    if (!addressFieldsDataView.value) {
        throw new Error("There were no address fields loaded yet");
    }

    const awaitableValidate = makeHandlerChainAwaitable<Parameters<typeof validateShippingAddressForm>[0], boolean>(
        validateShippingAddressForm,
    );
    const isShippingAddressValid = await awaitableValidate({
        address: props.shipToForEditing,
        validation: props.shipToForEditing.validation!,
        fieldDisplay: addressFieldsDataView.value.shipToAddressFields,
    })(props.dispatch, props.getState);

    const billTo = getBillToState(state, props.cart.billToId).value;
    if (!billTo) {
        throw new Error("The bill to for the current cart was not loaded.");
    }

    const isAdmin = session.userRoles.includes("Administrator");
    const isB2B = billTo?.properties?.isB2B === "True";
    const { b2BCanManageShippingAddress } = settings.settingsCollection?.customSettings as any;
    const canManageShipping = b2BCanManageShippingAddress === "true";

    const allowEdit = isAdmin || canManageShipping;
    const isRequestUpdate = !allowEdit;

    const isShippingAddressUpdateRequired = !isRequestUpdate && !isShippingAddressValid;

    props.dispatch({
        type: "Pages/CheckoutShipping/SetIsShippingAddressUpdateRequired",
        isShippingAddressUpdateRequired,
    });
};

replaceInChain(chain, ValidateShippingAddress, xValidateShippingAddress);
